import React from 'react';
import { useVersion } from '../../hooks/useVersion';
import { Link } from 'react-router-dom';
import { ExternalLink, ChevronRight, Shield, Lock, FileText, Cookie, Eye, Mail, Phone, MapPin, Database } from 'lucide-react';

export const HomeFooter = () => {
  const { version } = useVersion();
  
  return (
    <footer className="bg-[#2A2B2E] text-gray-300 pt-12 pb-6 border-t border-gray-700 z-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">OverSizeHub</h3>
            <p className="text-gray-400 mb-4">
              Comprehensive route survey management application for oversized and overweight transportation planning.
            </p>
            <div className="flex items-center gap-2 text-sm mb-2">
              <Mail className="w-4 h-4 text-gray-500" />
              <a href="mailto:info@oversizehub.net" className="hover:text-white transition-colors">
                info@oversizehub.net
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm mb-2">
              <Phone className="w-4 h-4 text-gray-500" />
              <a href="tel:+15555555555" className="hover:text-white transition-colors">
                +1 (555) 555-5555
              </a>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
              <address className="not-italic">
                123 Transport Way<br />
                Montreal, QC H3Z 2Y7<br />
                Canada
              </address>
            </div>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Legal Information</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" className="flex items-center gap-2 hover:text-white transition-colors">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span>Privacy Policy</span>
                </Link>
              </li>
              <li>
                <Link to="/terms-of-use" className="flex items-center gap-2 hover:text-white transition-colors">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span>Terms of Use</span>
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span>Disclaimer</span>
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Cookie className="w-4 h-4 text-gray-500" />
                  <span>Cookie Policy</span>
                </Link>
              </li>
              <li>
                <Link to="/accessibility" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Eye className="w-4 h-4 text-gray-500" />
                  <span>Accessibility Statement</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Security & Compliance */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Security & Compliance</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/security-policy" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Lock className="w-4 h-4 text-gray-500" />
                  <span>Security Policy</span>
                </Link>
              </li>
              <li>
                <Link to="/data-management" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Database className="w-4 h-4 text-gray-500" />
                  <span>Data Management Policy</span>
                </Link>
              </li>
              <li>
                <Link to="/copyright" className="flex items-center gap-2 hover:text-white transition-colors">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span>Copyright Information</span>
                </Link>
              </li>
              <li>
                <Link to="/compliance" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span>GDPR Compliance</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Data Sources */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Data Sources</h3>
            <ul className="space-y-2">
              <li>
                <a href="https://www.donneesquebec.ca/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <ExternalLink className="w-4 h-4 text-gray-500" />
                  <span>Données Québec</span>
                </a>
              </li>
              <li>
                <a href="https://www.transports.gouv.qc.ca/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <ExternalLink className="w-4 h-4 text-gray-500" />
                  <span>MTMD Clearance Directory</span>
                </a>
              </li>
              <li>
                <a href="https://www.quebec511.info/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <ExternalLink className="w-4 h-4 text-gray-500" />
                  <span>Québec 511</span>
                </a>
              </li>
              <li>
                <a href="https://511on.ca/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <ExternalLink className="w-4 h-4 text-gray-500" />
                  <span>Ontario 511</span>
                </a>
              </li>
              <li>
                <a href="https://www.ontario.ca/page/bridge-clearance-and-load-restriction-manual" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <ExternalLink className="w-4 h-4 text-gray-500" />
                  <span>MTO Bridge Clearance Manual</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright & Version */}
        <div className="border-t border-gray-700 pt-6 mt-6 flex flex-col md:flex-row justify-between items-center">
          <div className="text-sm text-gray-400 mb-4 md:mb-0">
            &copy; {new Date().getFullYear()} OverSizeHub. All rights reserved.
          </div>
          <div className="text-sm text-gray-400">
            Powered by Soltec - Version {version || '15.0'}
          </div>
        </div>
      </div>
    </footer>
  );
};